import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { parseApiError } from '@/api/parse-error';
import { captureMutationError } from '@/shared/lib/sentry-utils';
import { queryKeys, POLLING_INTERVALS } from '../query-keys';
import {
  getVenueConnections,
  getVenueConnection,
  createVenueConnection,
  updateVenueConnection,
  deleteVenueConnection,
  testVenueConnectivity,
  getVenueBalance,
  getVenueSymbols,
  getVenueConnectionConfig,
  getVenueLiveStatus,
  getVenueLiveHistory,
  confirmVenueLive,
  revokeVenueLive,
  initiateKeyRotation,
  getKeyRotationStatus,
  completeKeyRotation,
  abortKeyRotation,
  quickSetupConnection,
  getVenueConnectionsWithBalances,
  syncVenueBalance,
  getAllocationCapacity,
  type VenueConnectionListParams,
  type VenueConnectionCreate,
  type VenueConnectionUpdate,
  type ConfirmLiveRequest,
  type RevokeLiveRequest,
  type RotateCompleteRequest,
  type QuickSetupRequest,
} from '../endpoints/venue-connections';

export function useVenueConnections(params: VenueConnectionListParams = {}) {
  const withSystem = { include_system: true, ...params };
  return useQuery({
    queryKey: queryKeys.venueConnections.list(withSystem),
    queryFn: () => getVenueConnections(withSystem),
    select: (data) => data,
    refetchInterval: POLLING_INTERVALS.venueConnections,
    refetchIntervalInBackground: false,
  });
}

export function useVenueConnection(id: string) {
  return useQuery({
    queryKey: queryKeys.venueConnections.detail(id),
    queryFn: () => getVenueConnection(id),
    staleTime: 30_000,
  });
}

export function useCreateVenueConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VenueConnectionCreate) => createVenueConnection(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Venue connection created');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to create venue connection'));
      captureMutationError(err, { mutation: 'create_venue_connection' });
    },
  });
}

export function useUpdateVenueConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VenueConnectionUpdate) => updateVenueConnection(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Venue connection updated');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to update venue connection'));
      captureMutationError(err, { mutation: 'update_venue_connection', venue_connection_id: id });
    },
  });
}

export function useDeleteVenueConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVenueConnection(id),
    onSuccess: (_data, _id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Venue connection deleted');
    },
    onError: (err, _id) => {
      toast.error(parseApiError(err, 'Failed to delete venue connection'));
      captureMutationError(err, { mutation: 'delete_venue_connection', venue_connection_id: _id });
    },
  });
}

export function useTestVenueConnectivity() {
  return useMutation({
    mutationFn: (id: string) => testVenueConnectivity(id),
    onSuccess: (result) => {
      if (result.status === 'ok') {
        toast.success('Connectivity test passed');
      } else {
        toast.warning('Connectivity test failed');
      }
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to test connectivity'));
      captureMutationError(err, { mutation: 'test_venue_connectivity' });
    },
  });
}

export function useVenueBalance(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.venueConnections.balance(id),
    queryFn: () => getVenueBalance(id),
    refetchInterval: POLLING_INTERVALS.venueConnectionBalance,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useVenueSymbols(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.venueConnections.symbols(id),
    queryFn: () => getVenueSymbols(id),
    staleTime: POLLING_INTERVALS.symbolCatalog,
    enabled,
  });
}

export function useVenueConnectionConfig(id: string) {
  return useQuery({
    queryKey: queryKeys.venueConnections.config(id),
    queryFn: () => getVenueConnectionConfig(id),
    staleTime: 5 * 60_000,
  });
}

export function useVenueLiveStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.venueConnections.liveStatus(id),
    queryFn: () => getVenueLiveStatus(id),
    refetchInterval: POLLING_INTERVALS.killSwitch,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useVenueLiveHistory(id: string, params: { limit?: number; offset?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.venueConnections.liveHistory(id, params),
    queryFn: () => getVenueLiveHistory(id, params),
    staleTime: 60_000,
  });
}

export function useConfirmVenueLive(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mfaCode, ...body }: ConfirmLiveRequest & { mfaCode?: string }) =>
      confirmVenueLive(id, body, mfaCode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Live mode confirmed');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to confirm live mode'));
      captureMutationError(err, { mutation: 'confirm_venue_live', venue_connection_id: id });
    },
  });
}

export function useRevokeVenueLive(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RevokeLiveRequest) => revokeVenueLive(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.warning('Live mode revoked');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to revoke live mode'));
      captureMutationError(err, { mutation: 'revoke_venue_live', venue_connection_id: id });
    },
  });
}

export function useInitiateKeyRotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => initiateKeyRotation(id),
    onSuccess: (_data, _id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Key rotation initiated');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to initiate key rotation'));
      captureMutationError(err, { mutation: 'initiate_key_rotation' });
    },
  });
}

export function useKeyRotationStatus(id: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.venueConnections.rotateStatus(id),
    queryFn: () => getKeyRotationStatus(id),
    refetchInterval: POLLING_INTERVALS.rotateStatus,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useCompleteKeyRotation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RotateCompleteRequest) => completeKeyRotation(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Key rotation completed');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to complete key rotation'));
      captureMutationError(err, { mutation: 'complete_key_rotation', venue_connection_id: id });
    },
  });
}

// ── Wizard hooks (M1/M2) ────────────────────────────────────────────────────

export function useVenueConnectionsWithBalances(params: VenueConnectionListParams = {}) {
  const withSystem = { include_system: true, ...params };
  return useQuery({
    queryKey: queryKeys.venueConnections.listWithBalances(withSystem),
    queryFn: () => getVenueConnectionsWithBalances(withSystem),
    staleTime: 30_000,
  });
}

export function useQuickSetup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: QuickSetupRequest) => quickSetupConnection(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
    },
    onError: (err) => {
      captureMutationError(err, { mutation: 'quick_setup_connection' });
    },
  });
}

export function useSyncBalance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => syncVenueBalance(id),
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.balance(id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.venueConnections.listWithBalances(),
      });
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to sync balance'));
      captureMutationError(err, { mutation: 'sync_venue_balance' });
    },
  });
}

export function useAllocationCapacity(connectionId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.venueConnections.allocationCapacity(connectionId),
    queryFn: () => getAllocationCapacity(connectionId),
    staleTime: 30_000,
    enabled: enabled && !!connectionId,
  });
}

export function useAbortKeyRotation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => abortKeyRotation(id),
    onSuccess: (_data, _id) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venueConnections.all() });
      toast.success('Key rotation aborted');
    },
    onError: (err) => {
      toast.error(parseApiError(err, 'Failed to abort key rotation'));
      captureMutationError(err, { mutation: 'abort_key_rotation' });
    },
  });
}
